import sessionManager from '@/store/modules/session/sessionManager';
import {
  ROLES,
  MIXPANEL_TRACE_EVENT,
  MIXPANEL_TRACE_STRING,
  ROLES_DESCRIPTION,
  PUBLISH_TYPE_NORMAL
} from '@/constants';

import { plugIns as globalPlugIns, plugInAdapter } from '@/lib/globalPlugIns';
import { client } from '@/api';

import * as types from '../../mutationTypes';

import { getUserRoleText } from '../../../lib/util';
import { getRoleDescription } from '../../../lib/roleMap';

// Compare to (Token Expiry - Now)
// FIXME : This will break if Token Durations drop to 15 mins
// Perhaps these should be at 25% & 10% of the Token Duration instead?
const REFRESHTOKENTHRESHOLDMS = 15 * 60 * 1000;
const INTERRUPTREFRESHTOKENTHRESHOLDMS = 5 * 60 * 1000;
// For testing purposes:
// const REFRESHTOKENTHRESHOLDMS = 3600000 - (10 * 1000);
// const INTERRUPTREFRESHTOKENTHRESHOLDMS = 3600000 - (20 * 1000);

let onlyOneRefreshPlease = null;
let onlyOneInterruptPlease = null;

export const trackMixPanelPendingUpdatesEvent = (dispatch, plugIns, eventDetails) => {
  try {
    if (
      eventDetails.publishedUpdates &&
      eventDetails.publishedUpdates.appliedUpdates &&
      eventDetails.publishedUpdates.appliedUpdateIds
    ) {
      const commonMixpanelProperties = {
        appName: eventDetails.appName,
        client: eventDetails.client,
        subdomain: eventDetails.subdomain,
        userRole: MIXPANEL_TRACE_STRING.USER_ROLE_VIEW.CLIENT
      };

      eventDetails.publishedUpdates.appliedUpdateIds.forEach((id) => {
        const update = eventDetails.publishedUpdates.appliedUpdates[id];
        const foundModule = eventDetails.activatedCEModules.activatedCEModules.find(
          (ceModule) => ceModule.id === update.moduleId
        );

        dispatch('published/setPublishAt', { id, publishAt: update.createdAt }, { root: true });
        if (
          update.typeId === PUBLISH_TYPE_NORMAL &&
          (foundModule.clientId === null || foundModule.clientId === undefined)
        ) {
          const pendingUpdatesProperties = {
            publishId: update.publishId,
            publishSummary: update.publishSummary,
            typeId: update.typeId,
            publishAt: update.publishAt,
            moduleId: update.moduleId,
            moduleName: update.moduleName
          };
          const properties = plugInAdapter.Adapter.getCustomMixpanelProperties({
            ...commonMixpanelProperties,
            ...pendingUpdatesProperties
          });

          plugIns.mixPanelTrackEvent(MIXPANEL_TRACE_EVENT.PENDING_UPDATES, properties);
        }
      });
    }
  } catch (error) {
    console.error('Mixpanel Error: ', error);
  }
};

export default {
  async initialize({ commit, dispatch, rootState, rootGetters }, details = {}) {
    // FIXME: Vote to instantiate sessionManager as a singleton
    // prior to instantiating Vue.  This would eliminate this...
    // Careful, this does not behave synchronously with awaits in
    // our current startup sequence (login currently ok).
    // Awaits are promises, they yield to the event loop.
    // 1. The vue-router beforeEach is run before the App.vue beforeCreate
    // 2. We're currently pushing a function to the event loop in beforeEach
    // 3. That function routes back to login if user is not authenticated
    // We should probably move the sessionManager instantiation before
    // Vue is instantiated.
    // For full initialization, we have to await discovery of the IdP

    // Do the lightweight synchronous state mutations first
    const { skipSetIdpUri = false } = details;

    // Load all data that is common to all login pathways
    const subdomain = sessionManager.getSubdomain();
    commit(types.SET_SUBDOMAIN, subdomain);

    commit(types.SET_REDIRECT_URI, sessionManager.getIdpRedirectUrl());

    // This just returns window.location.hostname
    commit(types.SET_FQDN, sessionManager.getFQDN());

    if (sessionManager.isLoggedIn()) {
      dispatch('authorisation/saveUserRole', undefined, { root: true });
      dispatch('authorisation/saveOriginalAdminRole', undefined, { root: true });

      commit(types.SET_TOKEN_EXPIRY, sessionManager.getTokenExpiryFromLocalStorage());

      const profile = sessionManager.getProfile();
      if (profile) {
        commit(types.SET_PROFILE, JSON.parse(profile));
      }

      sessionManager.setAdditionalHeaders();

      await dispatch('authorisation/getSiteType', null, { root: true });
      const initialData = { userID: rootState.client.id }; // must run after getSiteType so that client.id is set
      dispatch('data/dataManagerSetup', initialData, { root: true });

      const getClientAccessDetailsPromise = dispatch('client/getAccessDetails', null, {
        root: true
      });

      const getClientContextPromise = dispatch('clientContext/getAll', null, { root: true });

      const diffms = sessionManager.getTokenExpiryFromLocalStorage() - new Date().getTime();
      const interruptms = diffms - INTERRUPTREFRESHTOKENTHRESHOLDMS;

      if (!onlyOneInterruptPlease) {
        onlyOneInterruptPlease = setTimeout(() => {
          // override refresh state, user has been idle
          commit(types.SET_REFRESH_TOKEN, false);
          commit(types.SET_INTERRUPT_REFRESH_TOKEN, true);
          onlyOneInterruptPlease = null;
        }, interruptms);
      }

      const refreshms = diffms - REFRESHTOKENTHRESHOLDMS;

      if (!onlyOneRefreshPlease) {
        onlyOneRefreshPlease = setTimeout(() => {
          commit(types.SET_REFRESH_TOKEN, true);
          onlyOneRefreshPlease = null;
        }, refreshms);
      }

      const getActivatedModules = dispatch('activatedCEModules/getAll', null, { root: true });

      await Promise.all([
        getClientAccessDetailsPromise,
        getClientContextPromise,
        getActivatedModules
      ]);

      commit(types.SET_SESSION_INITIALISED, true);
    } else if (rootGetters['authorisation/isLoggedIn']) {
      sessionManager.setAdditionalHeaders();

      await dispatch('authorisation/getSiteType', null, { root: true });
      const initialData = { userID: rootState.client.id }; // must run after getSiteType so that client.id is set
      dispatch('data/dataManagerSetup', initialData, { root: true });

      dispatch('authorisation/saveUserRole', undefined, { root: true });
      dispatch('authorisation/saveOriginalAdminRole', undefined, { root: true });

      const getClientAccessDetailsPromise = dispatch('client/getAccessDetails', null, {
        root: true
      });
      const getClientContextPromise = dispatch('clientContext/getAll', null, { root: true });

      const getActivatedModules = dispatch('activatedCEModules/getAll', null, { root: true });

      await Promise.all([
        getClientAccessDetailsPromise,
        getClientContextPromise,
        getActivatedModules
      ]);
    }

    if (!skipSetIdpUri) {
      sessionManager.setAdditionalHeaders();

      // Do async mutations last, we only need idp for login/token refreshing.
      const { idpUri } = await sessionManager.init();
      commit(types.SET_IDP_URI, idpUri);
    }
  },

  saveToken({ commit }, token) {
    sessionManager.saveToken(token);
    commit(types.SET_TOKEN, token);
  },

  setIsFirstLogin({ commit }, value) {
    commit(types.SET_IS_FIRST_LOGIN, value);
  },

  setIsLoginBannerShown({ commit }, value) {
    commit(types.SET_IS_LOGIN_BANNER_SHOWN, value);
  },

  // @complispace/cs-design-system uses this for the additional login callback handler.
  // Changes to the function name will break code in @complispace/cs-design-system for existing IDP login.
  async auth0LoginCallback({ dispatch, rootState, rootGetters }) {
    sessionManager.setAdditionalHeaders();
    let userRole;
    let appName;
    let subdomain;

    try {
      userRole = await dispatch('authorisation/getUserRole', undefined, { root: true });
    } catch (err) {
      dispatch('authorisation/logout', false, { root: true });
      throw new Error('Unable to retrieve user role, is the network/IdP down?');
    }

    try {
      const user = rootGetters['cs-session/user'];

      globalPlugIns.mixPanelUpdateUserProfile({
        email: user.email,
        name: user.getFullName(),
        userRole: getUserRoleText(userRole)
      });

      globalPlugIns.appcuesIdentifyUser({
        email: user.email,
        name: user.getFullName(),
        userRole: getUserRoleText(userRole)
      });

      appName = await sessionManager.getAppName();
      subdomain = sessionManager.getSubdomain();
      const properties = plugInAdapter.Adapter.getCustomMixpanelProperties({ appName, subdomain });

      globalPlugIns.mixPanelTrackEvent(MIXPANEL_TRACE_EVENT.LOG_IN, properties);

      const roleDescription = getRoleDescription(userRole);
      if (
        roleDescription !== ROLES_DESCRIPTION.CS_ADMIN &&
        roleDescription !== ROLES_DESCRIPTION.NOT_AUTHENTICATED
      ) {
        const userInformation = {
          firstName: user.name,
          lastName: user.surname,
          emailAddress: user.email,
          userRole: roleDescription
        };
        client.recordUserLoginInformation(userInformation);
      }
    } catch (err) {
      console.error(err);
    }

    if (userRole > 0) {
      dispatch('authorisation/saveUserRole', ROLES.CLIENT_USER, { root: true });
      dispatch('authorisation/saveOriginalAdminRole', userRole, { root: true });
    } else {
      dispatch('authorisation/logout', false, { root: true });
      throw new Error('Unexpected user role, is the user role set correctly?');
    }

    await dispatch('initialize', { skipSetIdpUri: true });

    if (userRole > ROLES.CLIENT_USER) {
      // On user login and the user has admin access. Trigger event to MixPanel with all pending updates.
      const eventDetails = {
        appName,
        client: rootState.client,
        subdomain,
        publishedUpdates: rootState.published,
        activatedCEModules: rootState.activatedCEModules
      };

      trackMixPanelPendingUpdatesEvent(dispatch, globalPlugIns, eventDetails);
    }

    await dispatch(types.SET_IS_FIRST_LOGIN, true); // set isFirstLogin to true when user perform login via auth0
  },

  async login({ commit, dispatch, state, rootState }, details) {
    // Make sure to re-initialize the state as the redirect will cause
    // a page reload.
    dispatch('logout');

    dispatch('authorisation/saveDraftMode', state.draftMode, { root: true });

    // Now we initialize knowing we're not logged in
    await dispatch('initialize');

    // Then make sessionManager.isLoggedIn() return true
    sessionManager.deleteAdditionalHeaders(); // Remove the x-site-uri header
    const token = sessionManager.getTokenFromUri(details.uri);
    dispatch('saveToken', token);

    commit(types.SET_TOKEN_EXPIRY, sessionManager.getTokenExpiryFromLocalStorage());

    const saveProfilePromise = sessionManager.saveProfile(state.idpUri);
    const userRolePromise = dispatch('authorisation/getUserRole', undefined, { root: true });

    const results = await Promise.all([saveProfilePromise, userRolePromise]);
    const userRole = results[1];

    // below require saveProfile to be completed first
    const profile = sessionManager.getProfile();
    if (profile) {
      commit(types.SET_PROFILE, JSON.parse(profile));
    }

    sessionManager.setAdditionalHeaders();

    let appName;
    let subdomain;
    const { plugIns } = details;

    try {
      const { name, email } = JSON.parse(profile);
      const fullName = name ? `${name.givenName} ${name.familyName}` : name;

      plugIns.mixPanelUpdateUserProfile({
        email,
        name: fullName,
        userRole: getUserRoleText(userRole),
        idpUri: state.idpUri
      });

      plugIns.appcuesIdentifyUser({
        email,
        name: fullName,
        userRole: getUserRoleText(userRole),
        idpUri: state.idpUri
      });

      appName = await sessionManager.getAppName();
      sessionManager.saveAppName(appName);
      subdomain = sessionManager.getSubdomain();
      const properties = plugInAdapter.Adapter.getCustomMixpanelProperties({ appName, subdomain });

      plugIns.mixPanelTrackEvent(MIXPANEL_TRACE_EVENT.LOG_IN, properties);

      const roleDescription = getRoleDescription(userRole);
      if (
        roleDescription !== ROLES_DESCRIPTION.CS_ADMIN &&
        roleDescription !== ROLES_DESCRIPTION.NOT_AUTHENTICATED
      ) {
        const userInformation = {
          firstName: name.givenName,
          lastName: name.familyName,
          emailAddress: email,
          userRole: roleDescription
        };
        client.recordUserLoginInformation(userInformation);
      }
    } catch (err) {
      console.error(err);
    }

    // below require userRole to be completed first
    if (userRole > 0) {
      // Default to Client User View
      dispatch('authorisation/saveUserRole', ROLES.CLIENT_USER, { root: true });
      dispatch('authorisation/saveOriginalAdminRole', userRole, { root: true });
      // because we have dispatched initialize earlier,
      // there is no need to mutate SET_IDP_URI again (saving 1 network call)
      await dispatch('initialize', { skipSetIdpUri: true });
      if (userRole > ROLES.CLIENT_USER) {
        // On user login and the user has admin access. Trigger event to MixPanel with all pending updates.
        const eventDetails = {
          appName,
          client: rootState.client,
          subdomain,
          publishedUpdates: rootState.published,
          activatedCEModules: rootState.activatedCEModules
        };

        trackMixPanelPendingUpdatesEvent(dispatch, plugIns, eventDetails);
      }
    } else {
      dispatch('logout');
      throw new Error('Unable to retrieve user role, is the network/IdP down?');
    }
    await dispatch(types.SET_IS_FIRST_LOGIN, true); // set isFirstLogin to true when user perform login via idp
  },

  async refresh({ commit, dispatch }, details) {
    // Then make sessionManager.isLoggedIn() return true
    // No need to interrupt, we've refreshed silently
    commit(types.SET_INTERRUPT_REFRESH_TOKEN, false);

    dispatch('clearTokenTimeouts');

    const token = sessionManager.getTokenFromUri(details.uri);
    dispatch('saveToken', token);

    const userRole = await dispatch('authorisation/getUserRole', undefined, { root: true });

    if (userRole > 0) {
      dispatch('authorisation/saveUserRole', userRole, { root: true });
      dispatch('authorisation/saveOriginalAdminRole', userRole, { root: true });

      commit(types.SET_TOKEN_EXPIRY, sessionManager.getTokenExpiryFromLocalStorage());

      const diffms = sessionManager.getTokenExpiryFromLocalStorage() - new Date().getTime();

      const interruptms = diffms - INTERRUPTREFRESHTOKENTHRESHOLDMS;

      if (!onlyOneInterruptPlease) {
        onlyOneInterruptPlease = setTimeout(() => {
          // override refresh state, user has been idle
          commit(types.SET_REFRESH_TOKEN, false);
          commit(types.SET_INTERRUPT_REFRESH_TOKEN, true);
          onlyOneInterruptPlease = null;
        }, interruptms);
      }

      const refreshms = diffms - REFRESHTOKENTHRESHOLDMS;

      if (!onlyOneRefreshPlease) {
        onlyOneRefreshPlease = setTimeout(() => {
          commit(types.SET_REFRESH_TOKEN, true);
          onlyOneRefreshPlease = null;
        }, refreshms);
      }
    } else {
      dispatch('logout');
      throw new Error('Unable to retrieve user role, is the network/IdP down?');
    }
  },

  clearTokenTimeouts() {
    if (onlyOneRefreshPlease) {
      window.clearTimeout(onlyOneRefreshPlease);
      onlyOneRefreshPlease = null;
    }
    if (onlyOneInterruptPlease) {
      window.clearTimeout(onlyOneInterruptPlease);
      onlyOneInterruptPlease = null;
    }
  },

  // @complispace/cs-design-system use this for the additional logout handler,
  // changes in here might break in @complispace/cs-design-system for existing idp login / logout
  logout({ commit, dispatch }) {
    dispatch('clearTokenTimeouts');
    sessionManager.clear();
    commit(types.AUTHORISATION_SET_ROLE, 0, { root: true });
    commit(types.AUTHORISATION_SET_ROLE_SET_ORIGINAL_ADMIN_ROLE, 0, { root: true });
  }
};
